'use client';

import ModalCloseButton from 'components/collection/modal-viewer/controls/close-button';
import { Modal3DDownloadButton } from 'components/collection/modal-viewer/controls/download-button';
import modalStyles from 'components/collection/modal-viewer/modal.module.scss';
import ModalBackground from 'components/collection/modal-viewer/modal-background';
import { type IVntanaAsset } from '@metmuseum/types';
import {
	VNTANA_EMBED_BASE_URL,
	VNTANA_ORGANIZATION_SLUG,
} from 'lib/constants.mjs';
import { getVntanaDownloadOptions } from 'lib/vntana/downloads';
import { useState, useRef, useEffect } from 'react';

// VNTANA shows its own loading screen inside the iframe before the model
// is ready. Wait this long after the iframe loads before revealing the
// download button so it doesn't appear over the loading state.
const VNTANA_UI_REVEAL_DELAY_MS = 2500;

const Modal3DViewer = ({
	vntanaAssets,
	alt,
	objectID,
	objectTitle,
	isOpenAccess,
	onClose,
}: IProps) => {
	const [isViewerReady, setIsViewerReady] = useState(false);
	const revealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleKeyDown = (e) => {
		switch (e.key) {
			case 'Escape':
				onClose();
				break;
		}
	};

	const handleIframeLoad = () => {
		if (revealTimeoutRef.current) {
			clearTimeout(revealTimeoutRef.current);
		}
		revealTimeoutRef.current = setTimeout(() => {
			setIsViewerReady(true);
		}, VNTANA_UI_REVEAL_DELAY_MS);
	};

	useEffect(() => {
		return () => {
			if (revealTimeoutRef.current) {
				clearTimeout(revealTimeoutRef.current);
			}
		};
	}, []);

	const asset = vntanaAssets?.[0];
	const downloadOptions = getVntanaDownloadOptions(asset, isOpenAccess);

	if (!asset) {
		return null;
	}

	const embedUrl = `${VNTANA_EMBED_BASE_URL}/v2?productUuid=${asset.uuid}&clientSlug=${asset.clientSlug}&organizationSlug=${VNTANA_ORGANIZATION_SLUG}`;

	return (
		<ModalBackground>
			<div className={modalStyles.modal} onKeyDown={handleKeyDown}>
				<div className={modalStyles.contentContainer}>
					<div style={{ width: '100%', height: '100%' }}>
						<iframe
							frameBorder="0"
							src={embedUrl}
							width="100%"
							height="100%"
							allow="fullscreen; xr-spatial-tracking"
							title={alt}
							style={{ border: 'none' }}
							onLoad={handleIframeLoad}
						/>
					</div>
					<div className={modalStyles.controls}>
						<ModalCloseButton handleClick={onClose} />
						{downloadOptions.length > 0 && isViewerReady && (
							<Modal3DDownloadButton
								downloadOptions={downloadOptions}
								objectID={objectID}
								objectTitle={objectTitle}
							/>
						)}
					</div>
				</div>
			</div>
		</ModalBackground>
	);
};

export default Modal3DViewer;

export interface IProps {
	alt: string;
	isOpenAccess: boolean;
	onClose: () => void;
	objectID?: string | number;
	objectTitle: string;
	vntanaAssets?: IVntanaAsset[] | null;
}
